import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { getReport, type SavedReport } from "../utils/reportManager";
import { exportTemplates } from "../export/templateRegistry";
import {
  CANVAS_HEIGHT,
  CANVAS_WIDTH,
  WidescreenExportCanvas,
} from "../export/components/WidescreenExportCanvas";
import { buildBrowserSavedReportRenderPayload } from "../export/renderPayload";
import { exportWidescreenCanvas } from "../utils/exportUtils";

export const ExportTemplatePage: React.FC = () => {
  const { templateId } = useParams();
  const [searchParams] = useSearchParams();
  const reportId = searchParams.get("id");
  const navigate = useNavigate();
  const [report, setReport] = useState<SavedReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(
    templateId ?? Object.keys(exportTemplates)[0],
  );

  const canvasRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!reportId) {
      navigate("/upload");
      return;
    }
    const saved = getReport(reportId);
    if (!saved) {
      setError("Report not found or has expired. Please upload again.");
      return;
    }
    const result = buildBrowserSavedReportRenderPayload({ savedReport: saved });
    if (!result.ok) {
      setError(
        result.errors
          .map((validationError) => validationError.message)
          .join("\n"),
      );
      return;
    }
    setReport(saved);
  }, [reportId, navigate]);

  useEffect(() => {
    if (templateId && exportTemplates[templateId]) {
      setSelectedTemplate(templateId);
    }
  }, [templateId]);

  const renderResult = report
    ? buildBrowserSavedReportRenderPayload({
        savedReport: report,
        templateId: selectedTemplate,
      })
    : null;
  const renderPayload = renderResult?.ok ? renderResult.payload : null;
  const template = renderPayload?.template;

  const handleExport = async () => {
    if (!canvasRef.current || !template) return;
    setIsExporting(true);
    try {
      await exportWidescreenCanvas(canvasRef.current, {
        fileName: `${report?.metadata?.client || "dashboard"}-${template.id}`,
        backgroundColor: template.background?.gradientFrom,
      });
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to export widescreen view.",
      );
    } finally {
      setIsExporting(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-[#f6f2ec] text-slate-900 flex items-center justify-center px-4 text-center">
        <div className="max-w-md space-y-4">
          <p className="text-lg font-semibold">{error}</p>
          <button
            onClick={() => navigate("/upload")}
            className="px-4 py-2 rounded-lg bg-slate-900 text-white font-semibold shadow-lg"
          >
            Return to Uploads
          </button>
        </div>
      </div>
    );
  }

  if (!report || !renderPayload || !template) {
    return (
      <div className="min-h-screen bg-[#f6f2ec] text-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-slate-300 border-t-slate-900 mx-auto mb-4"></div>
          <p>Loading export view...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f6f2ec] text-slate-900 px-6 py-8">
      <div className="max-w-[2100px] mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <button
            onClick={() =>
              navigate(`/upload/results?id=${reportId ?? report.id}`)
            }
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white text-slate-900 text-sm font-semibold shadow-sm border border-slate-200 hover:bg-slate-50 transition-colors"
          >
            ← Back to Results
          </button>

          <div className="flex flex-wrap items-center gap-3">
            <label className="text-xs uppercase tracking-[0.3em] text-slate-500">
              Template
            </label>
            <select
              value={selectedTemplate}
              onChange={(event) => setSelectedTemplate(event.target.value)}
              className="bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm shadow-sm"
            >
              {Object.values(exportTemplates).map((templateEntry) => (
                <option key={templateEntry.id} value={templateEntry.id}>
                  {templateEntry.name}
                </option>
              ))}
            </select>

            <button
              onClick={handleExport}
              disabled={isExporting}
              className={`inline-flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold transition-colors ${
                isExporting
                  ? "bg-slate-200 text-slate-400 cursor-not-allowed"
                  : "bg-emerald-500 text-white hover:bg-emerald-400 shadow-lg"
              }`}
            >
              {isExporting ? "Exporting…" : "Export PNG"}
            </button>
          </div>
        </div>

        <div className="text-slate-500 text-sm mb-4">
          {template.description} • Canvas {CANVAS_WIDTH}px × {CANVAS_HEIGHT}px
        </div>

        <div className="flex justify-center">
          <div className="rounded-[48px] bg-white border border-slate-100 shadow-[0_60px_140px_rgba(15,23,42,0.18)] p-8">
            <WidescreenExportCanvas
              ref={canvasRef}
              data={renderPayload.data}
              template={renderPayload.template}
              callouts={renderPayload.callouts}
              metadata={renderPayload.metadata}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
